import React from 'react'
import { CustomTooltip, type CustomTooltipProps } from '~/components/common/CustomTooltip'
import { cm } from '~/utils/utils'

export interface HoverTextProps
  extends Omit<CustomTooltipProps, 'children' | 'classNames' | 'content'> {
  /** The full text to show on hover. */
  content: React.ReactNode
  /** The truncated or abbreviated text trigger. */
  children: React.ReactNode
  /** Classes for the trigger wrapper. */
  className?: string
  /** Classes for the white hover surface. */
  contentClassName?: string
}

/**
 * Use HoverText when visible text is truncated or abbreviated and hover should
 * reveal the complete value. For explanatory help, status hints, or arbitrary
 * rich content, use CustomTooltip/Tooltip directly so the intent stays clear.
 */
export function HoverText({
  content,
  children,
  className,
  contentClassName,
  placement = 'bottom',
  showArrow = false,
  delay = 0,
  closeDelay = 0,
  ...props
}: HoverTextProps) {
  return (
    <CustomTooltip
      content={content}
      placement={placement}
      showArrow={showArrow}
      delay={delay}
      closeDelay={closeDelay}
      classNames={{
        base: 'before:!bg-tokens-neutral-bg-white before:!border-transparent',
        content: cm(
          '!w-max !max-w-[min(760px,calc(100vw-2rem))] !overflow-hidden !text-ellipsis !whitespace-nowrap !rounded-lg !border !border-tokens-neutral-border-2 !bg-tokens-neutral-bg-white !px-3 !py-2 !text-base !font-semibold !leading-[22px] !text-tokens-neutral-text-primary !shadow-popover',
          contentClassName
        )
      }}
      {...props}
    >
      <span className={cm('block min-w-0', className)}>{children}</span>
    </CustomTooltip>
  )
}

export default HoverText
